import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, CheckCircle, Database, Cloud, Settings, BarChart3, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  {
    icon: Database,
    title: 'Oracle ERP Implementation',
    description: 'Ready-to-deploy and customized ERP solutions that transform your operations.',
    link: '/services#erp'
  },
  {
    icon: Settings,
    title: 'Custom Oracle Apps',
    description: 'Bespoke Oracle applications tailored to your unique business processes.',
    link: '/services#custom'
  },
  {
    icon: Cloud,
    title: 'Cloud Migration',
    description: 'Seamless transitions to Oracle Cloud Infrastructure with zero downtime.',
    link: '/services#cloud'
  },
  {
    icon: BarChart3,
    title: 'Managed Services',
    description: '24/7 support and optimization for your Oracle ecosystem.',
    link: '/services#managed'
  },
  {
    icon: Shield,
    title: 'Enterprise Transformation',
    description: 'End-to-end digital transformation programs for enterprise growth.',
    link: '/services'
  }
];

const stats = [
  { value: '100+', label: 'Enterprise Clients' },
  { value: '98%', label: 'Client Retention' },
  { value: '10+', label: 'Years Experience' }
];

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [testimonialsRes, caseStudiesRes] = await Promise.all([
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/case-studies`)
        ]);
        setTestimonials(testimonialsRes.data);
        setCaseStudies(caseStudiesRes.data.filter(cs => cs.is_featured));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="pt-20" data-testid="home-page">
      <SEO 
        title="Oracle ERP Implementation & Cloud Migration"
        description="End to end Oracle ERP implementation, Cloud migration and Custom solutions for enterprise companies globally. Trusted by leading global enterprises."
        keywords="Oracle ERP, ERP Implementation, Cloud Migration, Oracle Solutions, Enterprise Software, Digital Transformation, Oracle Consulting"
      />
      {/* Hero Section - Dark */}
      <section className="section-dark hero-pattern min-h-[85vh] md:min-h-[90vh] flex items-center relative overflow-hidden">
        {/* Background glow effect */}
        <div className="absolute top-1/2 left-1/4 w-48 md:w-96 h-48 md:h-96 bg-oracle-red/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-32 md:w-64 h-32 md:h-64 bg-oracle-red/5 rounded-full blur-3xl" />
        
        <div className="container-custom py-12 md:py-20 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="animate-fade-in-up text-center lg:text-left">
              <span className="eyebrow mb-3 md:mb-4 inline-block text-xs md:text-sm">Oracle Transformation Partner</span>
              <h1 className="heading-xl text-white mb-4 md:mb-6">
                Enterprise Excellence
                <span className="block text-oracle-red">Delivered</span>
              </h1>
              <p className="text-slate-300 font-body text-base md:text-lg lg:text-xl leading-relaxed mb-6 md:mb-8 max-w-xl mx-auto lg:mx-0">
                Transform your enterprise with expert Oracle implementations, cloud migrations and custom solutions.
                <span className="block mt-3 md:mt-4">Trusted by leading global enterprises worldwide.</span>
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start">
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button 
                    size="lg" 
                    className="w-full sm:w-auto bg-oracle-red hover:bg-oracle-red-dark text-white font-body text-sm md:text-base px-6 md:px-8 py-5 md:py-6"
                    data-testid="hero-consultation-btn"
                  >
                    Schedule Consultation
                    <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
                  </Button>
                </Link>
                <Link to="/case-studies" className="w-full sm:w-auto">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="w-full sm:w-auto border-slate-600 text-white hover:bg-slate-800 font-body text-sm md:text-base px-6 md:px-8 py-5 md:py-6"
                    data-testid="hero-case-studies-btn"
                  >
                    View Case Studies
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-3 lg:grid-cols-2 gap-3 md:gap-6 animate-fade-in mt-6 lg:mt-0" style={{ animationDelay: '0.3s' }}>
              {stats.map((stat, index) => (
                <div 
                  key={stat.label}
                  className="card-dark p-4 md:p-6 lg:p-8 text-center"
                  style={{ animationDelay: `${0.4 + index * 0.1}s` }}
                  data-testid={`stat-${index}`}
                >
                  <div className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-oracle-red mb-1 md:mb-2">
                    {stat.value}
                  </div>
                  <div className="font-body text-xs md:text-sm text-slate-400 uppercase tracking-wider">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Light */}
      <section className="section-light py-12 md:py-20 lg:py-32" data-testid="services-section">
        <div className="container-custom">
          <div className="text-center mb-10 md:mb-16">
            <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">What We Do</span>
            <h2 className="heading-lg text-slate-900 mb-3 md:mb-4 font-heading">
              Comprehensive Oracle Solutions
            </h2>
            <p className="text-slate-600 font-body text-sm md:text-base lg:text-lg max-w-2xl mx-auto px-2">
              From implementation to optimization, we deliver end-to-end Oracle solutions that drive measurable business outcomes.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            {services.map((service, index) => (
              <Link 
                key={service.title} 
                to={service.link}
                className="group"
              >
                <Card 
                  className="h-full card-enterprise card-hover-lift service-card-border"
                  data-testid={`service-card-${index}`}
                >
                  <CardContent className="p-5 md:p-6 lg:p-8">
                    <service.icon className="w-10 h-10 md:w-12 md:h-12 text-oracle-red mb-4 md:mb-6 group-hover:scale-110 transition-transform" />
                    <h3 className="font-heading text-lg md:text-xl font-bold text-slate-900 mb-2 md:mb-3">
                      {service.title}
                    </h3>
                    <p className="font-body text-slate-600 text-xs md:text-sm leading-relaxed">
                      {service.description}
                    </p>
                    <div className="mt-4 md:mt-6 flex items-center text-oracle-red font-body text-xs md:text-sm font-medium group-hover:translate-x-2 transition-transform">
                      Learn More <ArrowRight className="ml-2 w-3 h-3 md:w-4 md:h-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Case Studies - Dark */}
      <section className="section-dark py-12 md:py-20 lg:py-32" data-testid="case-studies-section">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 md:mb-12">
            <div className="text-center md:text-left w-full md:w-auto">
              <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">Success Stories</span>
              <h2 className="heading-lg text-white font-heading">
                Transformation Results
              </h2>
            </div>
            <Link to="/case-studies" className="w-full md:w-auto mt-4 md:mt-0">
              <Button 
                variant="outline" 
                className="w-full md:w-auto border-slate-600 text-white hover:bg-slate-800 font-body text-sm"
                data-testid="view-all-case-studies-btn"
              >
                View All Case Studies
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
            {caseStudies.slice(0, 2).map((study, index) => (
              <Link 
                key={study.id} 
                to={`/case-studies/${study.id}`}
                className="group"
              >
                <Card 
                  className="card-dark overflow-hidden h-full"
                  data-testid={`featured-case-study-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={study.image_url || 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800'}
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-4 md:p-6 lg:p-8">
                    <span className="eyebrow mb-2 md:mb-3 inline-block text-xs">{study.category.toUpperCase()}</span>
                    <h3 className="font-heading text-lg md:text-xl font-bold text-white mb-3 md:mb-4 group-hover:text-oracle-red transition-colors">
                      {study.title}
                    </h3>
                    <p className="font-body text-slate-400 text-xs md:text-sm mb-4 md:mb-6 line-clamp-2">
                      {study.challenge}
                    </p>
                    <div className="flex flex-wrap gap-2 md:gap-4">
                      {study.metrics?.slice(0, 3).map((metric, i) => (
                        <div key={i} className="bg-slate-800 px-3 md:px-4 py-1.5 md:py-2 rounded-sm">
                          <div className="font-heading text-base md:text-lg font-bold text-oracle-red">
                            {metric.value}
                          </div>
                          <div className="font-body text-[10px] md:text-xs text-slate-400">
                            {metric.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section - Dark with Background */}
      <section className="section-dark py-12 md:py-20 lg:py-32 relative overflow-hidden" data-testid="testimonials-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1920" 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-deep-obsidian via-deep-obsidian/95 to-deep-obsidian" />
        
        <div className="container-custom relative z-10">
          <div className="text-center mb-8 md:mb-12">
            <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">Client Feedback</span>
            <h2 className="heading-lg text-white font-heading">
              Trusted by Industry Leaders
            </h2>
          </div>
          
          {testimonials.length > 0 && (
            <Carousel className="max-w-4xl mx-auto px-2">
              <CarouselContent>
                {testimonials.map((testimonial, index) => (
                  <CarouselItem key={testimonial.id}>
                    <Card className="border border-slate-700 bg-slate-900/50 backdrop-blur-sm mx-1" data-testid={`testimonial-${index}`}>
                      <CardContent className="p-5 md:p-8 lg:p-10 text-center">
                        <div className="text-oracle-red text-4xl md:text-5xl lg:text-6xl font-heading leading-none mb-4 md:mb-6">"</div>
                        <p className="font-body text-base md:text-lg lg:text-2xl text-slate-200 leading-relaxed mb-6 md:mb-8">
                          {testimonial.quote}
                        </p>
                        <div className="text-center">
                          <div className="font-heading text-base md:text-lg font-bold text-white">
                            {testimonial.title}
                          </div>
                          <div className="font-body text-xs md:text-sm text-slate-400">
                            {testimonial.company}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="hidden md:flex border-slate-600 text-white hover:bg-slate-800 -left-4 lg:-left-12" />
              <CarouselNext className="hidden md:flex border-slate-600 text-white hover:bg-slate-800 -right-4 lg:-right-12" />
            </Carousel>
          )}
          
          <div className="text-center mt-8 md:mt-10">
            <Link to="/testimonials">
              <Button 
                variant="outline" 
                className="border-slate-600 text-white hover:bg-slate-800 font-body text-sm"
                data-testid="view-all-testimonials-btn"
              >
                Read More Testimonials
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section - Dark */}
      <section className="section-dark py-12 md:py-20 lg:py-32 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-oracle-red/20 to-transparent" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center lg:text-left lg:mx-0">
            <span className="eyebrow mb-3 md:mb-4 inline-block text-xs">Ready to Transform?</span>
            <h2 className="heading-lg text-white mb-4 md:mb-6 font-heading">
              Start Your Oracle Journey Today
            </h2>
            <p className="text-slate-300 font-body text-sm md:text-base lg:text-lg mb-6 md:mb-8">
              Schedule a consultation with our Oracle experts and discover how we can accelerate your enterprise transformation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center lg:justify-start">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-oracle-red hover:bg-oracle-red-dark text-white font-body px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
                  data-testid="cta-consultation-btn"
                >
                  Schedule Consultation
                </Button>
              </Link>
              <Link to="/contact" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-slate-900 font-body px-6 md:px-8 py-5 md:py-6 text-sm md:text-base"
                  data-testid="cta-proposal-btn"
                >
                  Request Proposal
                </Button>
              </Link>
            </div>
            
            <div className="mt-8 md:mt-12 flex flex-col sm:flex-row flex-wrap items-center justify-center lg:justify-start gap-4 md:gap-6">
              {[
                'Free Initial Consultation',
                'Custom Solution Design',
                'ROI Analysis Included'
              ].map((item) => (
                <div key={item} className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-oracle-red" />
                  <span className="font-body text-xs md:text-sm text-slate-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
